import React, { useEffect, useRef, useState } from 'react';
import { UploadCloudIcon, SparklesIcon, DownloadIcon, RefreshCcwIcon, AlertTriangleIcon, Loader2Icon, WandIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { Slider } from '../ui/Slider';
import { ApiKeyPanel } from './ApiKeyPanel';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { getStoredApiKey } from '../../lib/interiorAI/apiKeyStore';
import { buildInteriorPrompt } from '../../lib/interiorAI/promptBuilder';
import { fileToBase64, transformImageWithGemini, GeminiApiError } from '../../lib/interiorAI/geminiImageProvider';
const ROOM_OPTIONS = [{
  value: 'obyvacka',
  label: 'Obývacia izba'
}, {
  value: 'kuchyna',
  label: 'Kuchyňa'
}, {
  value: 'spalna',
  label: 'Spálňa'
}, {
  value: 'detska',
  label: 'Detská izba'
}, {
  value: 'kupelna',
  label: 'Kúpeľňa a WC'
}];
const STYLE_OPTIONS = [{
  value: 'minimalisticky',
  label: 'Minimalistický'
}, {
  value: 'skandinavsky',
  label: 'Škandinávsky'
}, {
  value: 'industrialny',
  label: 'Industriálny'
}, {
  value: 'luxusny',
  label: 'Moderný luxusný'
}, {
  value: 'boho',
  label: 'Boho'
}];
const COLORS = [{
  id: 'neutral',
  class: 'bg-[#E5E5EA]',
  label: 'Neutrálna'
}, {
  id: 'warm',
  class: 'bg-[#D4B59E]',
  label: 'Teplá'
}, {
  id: 'cool',
  class: 'bg-[#A8C0D8]',
  label: 'Studená'
}, {
  id: 'dark',
  class: 'bg-[#2C3E50]',
  label: 'Tmavá'
}, {
  id: 'nature',
  class: 'bg-[#8F9779]',
  label: 'Prírodná'
}];

// Curated demo "after" shots used only when no Gemini API key is connected,
// so the plugin still feels alive out of the box.
const DEMO_RESULTS: Record<string, string> = {
  obyvacka: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=900&h=675&fit=crop&crop=center',
  kuchyna: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=900&h=675&fit=crop&crop=center',
  spalna: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=900&h=675&fit=crop&crop=center',
  detska: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=900&h=675&fit=crop&crop=center',
  kupelna: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=900&h=675&fit=crop&crop=center'
};
function labelFor(options: {
  value: string;
  label: string;
}[], value: string) {
  return options.find(o => o.value === value)?.label || value;
}
export function PhotoTransformerPlugin() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [apiKey, setApiKey] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [roomType, setRoomType] = useState<string | string[]>('obyvacka');
  const [style, setStyle] = useState<string | string[]>('minimalisticky');
  const [colorId, setColorId] = useState('neutral');
  const [budget, setBudget] = useState<number | [number, number]>(3000);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    src: string;
    isDemo: boolean;
  } | null>(null);
  useEffect(() => {
    setApiKey(getStoredApiKey());
  }, []);
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);
  const handleFile = (selected: File | null) => {
    if (!selected) return;
    if (!selected.type.startsWith('image/')) {
      setError('Nahraj prosím súbor vo formáte obrázka (JPG, PNG).');
      return;
    }
    setError(null);
    setResult(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFile(e.dataTransfer.files?.[0] || null);
  };
  const handleReset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    setResult(null);
    setError(null);
  };
  const handleGenerate = async () => {
    if (!file || !previewUrl) {
      setError('Najprv nahraj fotografiu svojho priestoru.');
      return;
    }
    setError(null);
    setIsLoading(true);
    const roomLabel = labelFor(ROOM_OPTIONS, Array.isArray(roomType) ? roomType[0] : roomType);
    const styleLabel = labelFor(STYLE_OPTIONS, Array.isArray(style) ? style[0] : style);
    const colorLabel = COLORS.find(c => c.id === colorId)?.label || 'neutrálna';
    const budgetValue = Array.isArray(budget) ? budget[1] : budget;
    const budgetLabel = `€${budgetValue.toLocaleString('sk-SK')}`;
    try {
      if (apiKey) {
        const {
          base64,
          mimeType
        } = await fileToBase64(file);
        const prompt = buildInteriorPrompt({
          roomLabel,
          styleLabel,
          colorLabel,
          budgetLabel
        });
        const transformed = await transformImageWithGemini({
          apiKey,
          imageBase64: base64,
          mimeType,
          prompt
        });
        setResult({
          src: `data:${transformed.mimeType};base64,${transformed.imageBase64}`,
          isDemo: false
        });
      } else {
        // Demo mode — no API key connected yet.
        await new Promise(resolve => setTimeout(resolve, 2200));
        const demoSrc = DEMO_RESULTS[Array.isArray(roomType) ? roomType[0] : roomType] || DEMO_RESULTS.obyvacka;
        setResult({
          src: demoSrc,
          isDemo: true
        });
      }
    } catch (err) {
      if (err instanceof GeminiApiError) {
        setError(err.message);
      } else {
        setError('Niečo sa pokazilo pri generovaní dizajnu. Skús to prosím znova.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleDownload = () => {
    if (!result) return;
    const link = document.createElement('a');
    link.href = result.src;
    link.download = 'interiorai-navrh.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return <div className="reveal bg-white rounded-[2.5rem] shadow-2xl border border-gray-100 overflow-hidden" data-id="element-219" data-mp="2qnhiu9ok5e4g">
      <div className="p-8 md:p-12 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4" data-id="element-220" data-mp="3gbwu7bef9vvu">
        <div data-id="element-221" data-mp="3pa8j98zn4lm6">
          <div className="flex items-center gap-2 mb-2" data-id="element-222" data-mp="3p9p7yb4hwvh4">
            <WandIcon className="w-5 h-5 text-apple-blue" data-id="element-223" data-mp="3pcbfwbvatczv" />
            <h3 className="text-2xl font-bold text-apple-black" data-id="element-224" data-mp="3uacx0r95z28p">Interior AI Plugin — živá ukážka</h3>
          </div>
          <p className="text-sm text-apple-gray font-light" data-id="element-225" data-mp="32inhqoqouq5">
            Nahraj fotku, vyber štýl a nechaj AI pretvoriť tvoj priestor priamo v prehliadači.
          </p>
        </div>
        <span className="flex items-center gap-1.5 bg-blue-50 text-apple-blue text-xs font-bold px-3 py-1.5 rounded-full" data-id="element-226" data-mp="bwdiw70276sl">
          <SparklesIcon className="w-3.5 h-3.5" data-id="element-227" data-mp="gtney5hyegpe" />
          Poháňané Gemini 2.5 Flash Image
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-228" data-mp="lrf97662mewr">
        {/* Left: Upload / Preview / Result */}
        <div className="p-8 md:p-12 bg-gray-50/50 border-b lg:border-b-0 lg:border-r border-gray-100 flex flex-col justify-center" data-id="element-229" data-mp="qq8jkpq29psp">
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files?.[0] || null)} data-id="element-230" data-mp="26b117czvc2wf" />

          {!previewUrl && <div onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }} onDragLeave={() => setIsDragging(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-2xl bg-white p-12 flex flex-col items-center justify-center text-center cursor-pointer transition-colors group ${isDragging ? 'border-apple-blue bg-blue-50/40' : 'border-gray-300 hover:border-apple-blue hover:bg-blue-50/30'}`} data-id="element-231" data-mp="1wf7gh6bzx33k">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-232" data-mp="37z36pqa6y6m1">
                <UploadCloudIcon className="w-10 h-10 text-apple-blue" data-id="element-233" data-mp="2l53asdzv0hxa" />
              </div>
              <p className="text-lg font-medium text-apple-black mb-2" data-id="element-234" data-mp="27dg6bqvrk33f">Potiahni fotku sem</p>
              <p className="text-sm text-apple-gray mb-6" data-id="element-235" data-mp="2cayfnidp0kby">
                alebo klikni pre výber súboru (JPG, PNG, max 10MB)
              </p>
              <Button variant="secondary" className="!rounded-full" data-id="element-236" data-mp="3023r7lcloqgg">
                Vybrať súbor
              </Button>
            </div>}

          {previewUrl && !result && !isLoading && <div className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-100" data-id="element-237" data-mp="2q58xdfr9ve8i">
              <img src={previewUrl} alt="Nahraná izba" className="w-full aspect-[4/3] object-cover" data-id="element-238" data-mp="2v4c1mxzfhgff" />
              <button onClick={handleReset} className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-apple-black text-xs font-bold px-3 py-1.5 rounded-full shadow-md hover:bg-white transition-colors" data-id="element-239" data-mp="1eq2fmains3rr">
                Zmeniť fotku
              </button>
            </div>}

          {previewUrl && isLoading && <div className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-100" data-id="element-240" data-mp="1l3ge3f3n8fne">
              <img src={previewUrl} alt="Spracovávanie" className="w-full aspect-[4/3] object-cover opacity-40 blur-[2px]" data-id="element-241" data-mp="1g5y1neqf3ian" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-white/30" data-id="element-242" data-mp="sd7vjm573t8a">
                <Loader2Icon className="w-10 h-10 text-apple-blue animate-spin" data-id="element-243" data-mp="125dlkqbw00ku" />
                <span className="bg-white/90 backdrop-blur-sm text-apple-black text-sm font-bold px-4 py-2 rounded-full shadow-md" data-id="element-244" data-mp="1278643j8xqoe">
                  AI premýšľa nad tvojím priestorom...
                </span>
              </div>
            </div>}

          {previewUrl && result && !isLoading && <div className="space-y-3" data-id="element-245" data-mp="3mr9yo4fw7255">
              <BeforeAfterSlider beforeSrc={previewUrl} afterSrc={result.src} data-id="element-246" data-mp="3mq8p64eiynpj" />
              {result.isDemo && <p className="text-xs text-amber-600 font-semibold flex items-center gap-1.5" data-id="element-247" data-mp="3hsqdw7d1bwed">
                  <SparklesIcon className="w-3.5 h-3.5" data-id="element-248" data-mp="3crj4k3v8up5l" />
                  Demo náhľad — pripoj Gemini API kľúč nižšie pre reálnu AI transformáciu tvojej fotky.
                </p>}
            </div>}
        </div>

        {/* Right: Settings */}
        <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-249" data-mp="2z1fo5tj1ppo2">
          <h3 className="text-2xl font-bold text-apple-black mb-8" data-id="element-250" data-mp="2u2m9hxs0vsmb">Nastavenia dizajnu</h3>

          <div className="space-y-7" data-id="element-251" data-mp="2u09thq5trsbw">
            <div data-id="element-252" data-mp="2p4b0fp9jxm55">
              <label className="block text-sm font-semibold text-apple-black mb-3" data-id="element-253" data-mp="2p4uhz4vai8in">Typ priestoru</label>
              <Select options={ROOM_OPTIONS} value={roomType} onChange={setRoomType} placeholder="Vyberte typ miestnosti" data-id="element-254" data-mp="2l0mu19wnowlh" />
            </div>

            <div data-id="element-255" data-mp="vkj16eanfuer">
              <label className="block text-sm font-semibold text-apple-black mb-3" data-id="element-256" data-mp="vn5e6tgb724d">Štýl interiéru</label>
              <Select options={STYLE_OPTIONS} value={style} onChange={setStyle} placeholder="Vyberte štýl" data-id="element-257" data-mp="14lh937o7km2m" />
            </div>

            <div data-id="element-258" data-mp="cukxeadvwngx">
              <label className="block text-sm font-semibold text-apple-black mb-3" data-id="element-259" data-mp="cv440uy33pfn">Farebná paleta</label>
              <div className="flex flex-wrap gap-4" data-id="element-260" data-mp="gv4w3ie3vz56">
                {COLORS.map(color => <button key={color.id} type="button" onClick={() => setColorId(color.id)} className="flex flex-col items-center gap-2 focus:outline-none group" data-id="element-261" data-mp="lur5fgqtcv8p">
                    <div className={`w-12 h-12 rounded-full ${color.class} shadow-sm border-2 transition-all ${colorId === color.id ? 'border-apple-blue scale-110 ring-4 ring-blue-100' : 'border-transparent group-hover:scale-105'}`} data-id="element-262" data-mp="3p9vhte7zuo11" />
                    <span className={`text-xs font-medium ${colorId === color.id ? 'text-apple-blue' : 'text-apple-gray'}`} data-id="element-263" data-mp="1a7up8vdw9cxc">
                      {color.label}
                    </span>
                  </button>)}
              </div>
            </div>

            <div data-id="element-264" data-mp="dkgad2nw1o1f">
              <div className="flex justify-between items-center mb-3" data-id="element-265" data-mp="ri4uu9e1mt5y">
                <label className="block text-sm font-semibold text-apple-black" data-id="element-266" data-mp="rg1xf4ak9xr7">Odhadovaný rozpočet</label>
                <span className="text-sm font-bold text-apple-blue" data-id="element-267" data-mp="mlejfzd6olqv">
                  €{(Array.isArray(budget) ? budget[1] : budget).toLocaleString('sk-SK')}
                </span>
              </div>
              <div className="px-2" data-id="element-268" data-mp="1y0l0t3hvxu73">
                <Slider min={500} max={20000} step={500} value={budget} onChange={setBudget} showLabels={false} data-id="element-269" data-mp="26ezhoawp2wd6" />
              </div>
              <div className="flex justify-between text-xs text-apple-gray mt-2" data-id="element-270" data-mp="2aidwwzct78pk">
                <span data-id="element-271" data-mp="1wi343qrxvk1r">€500</span>
                <span data-id="element-272" data-mp="1wj5xq6gnc0ld">€20,000+</span>
              </div>
            </div>

            {error && <div className="flex items-start gap-2.5 bg-red-50 border border-red-100 text-red-700 text-sm font-medium rounded-xl p-4" data-id="element-273" data-mp="21i8s8wz02of3">
                <AlertTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-274" data-mp="2u5bmve0k2f02" />
                <span data-id="element-275" data-mp="2ffxcq3p5ntjm">{error}</span>
              </div>}

            <div className="flex flex-col sm:flex-row gap-3" data-id="element-276" data-mp="2ffnl6bogpebt">
              <Button size="large" onClick={handleGenerate} disabled={isLoading} className="flex-1 !bg-apple-black hover:!bg-gray-800 !text-white !rounded-xl !py-4 !font-bold shadow-lg disabled:opacity-60" data-id="element-277" data-mp="2ke8l09zsxvzq">
                {isLoading ? 'Generujem…' : 'Vygenerovať dizajn'}
              </Button>
              {result && <Button size="large" variant="secondary" onClick={handleDownload} className="!rounded-xl !py-4 !font-bold flex items-center justify-center gap-2" data-id="element-278" data-mp="1k6u0jtmrd2ud">
                  <DownloadIcon className="w-4 h-4" data-id="element-279" data-mp="1ac244t4wwm16" />
                  Stiahnuť
                </Button>}
              {result && <Button size="large" variant="secondary" onClick={handleReset} className="!rounded-xl !py-4 !font-bold flex items-center justify-center gap-2" data-id="element-280" data-mp="22zfto66a6fb9">
                  <RefreshCcwIcon className="w-4 h-4" data-id="element-281" data-mp="1t6qsc44g2htn" />
                  Nová fotka
                </Button>}
            </div>

            <ApiKeyPanel apiKey={apiKey} onApiKeyChange={setApiKey} data-id="element-282" data-mp="2lwz6lkvv0i2y" />
          </div>
        </div>
      </div>
    </div>;
}